import { useApp } from "../context/AppContext";
import { fmtPrice } from "../lib/i18n";
import { X, Minus, Plus, Trash2 } from "lucide-react";
import { useNavigate } from "react-router-dom";

export const CartDrawer = () => {
    const { cart, cartOpen, setCartOpen, cartTotal, updateQty, removeFromCart, lang, t } = useApp();
    const navigate = useNavigate();

    if (!cartOpen) return null;

    const handleCheckout = () => {
        setCartOpen(false);
        navigate("/checkout");
    };

    return (
        <div className="fixed inset-0 z-[60]" data-testid="cart-drawer">
            <div
                onClick={() => setCartOpen(false)}
                className="absolute inset-0 bg-black/70 backdrop-blur-sm"
            />
            <aside
                className={`absolute top-0 ${lang === 'ar' ? 'left-0' : 'right-0'} h-full w-full max-w-md bg-[#0A0A0A] border-s border-[#D4AF37]/20 flex flex-col`}
            >
                <div className="flex items-center justify-between px-6 py-5 border-b border-[#D4AF37]/20">
                    <h3 className="section-heading text-xl text-white">{t.cart.title}</h3>
                    <button onClick={() => setCartOpen(false)} data-testid="cart-close" className="text-white/70 hover:text-[#D4AF37]">
                        <X size={22} />
                    </button>
                </div>

                <div className="flex-1 overflow-y-auto px-6 py-6">
                    {cart.length === 0 ? (
                        <div className="h-full flex flex-col items-center justify-center text-center gap-2">
                            <p className="text-white/70 text-lg font-serif">{t.cart.empty}</p>
                            <p className="text-white/40 text-sm">{t.cart.empty_sub}</p>
                        </div>
                    ) : (
                        <ul className="space-y-5">
                            {cart.map((item) => (
                                <li
                                    key={`${item.product_id}-${item.variant_label || ''}`}
                                    data-testid={`cart-item-${item.product_id}`}
                                    className="flex gap-4 pb-5 border-b border-[#D4AF37]/10"
                                >
                                    <div className="w-20 h-24 bg-[#141414] border border-[#D4AF37]/20 overflow-hidden flex-shrink-0">
                                        {item.image && (
                                            <img src={item.image} alt={item.product_name} className="w-full h-full object-cover" />
                                        )}
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <div className="flex justify-between gap-2">
                                            <div>
                                                <p className="text-white text-sm font-medium truncate">{item.product_name}</p>
                                                {item.variant_label && (
                                                    <p className="text-[#D4AF37]/80 text-xs mt-0.5">{item.variant_label}</p>
                                                )}
                                            </div>
                                            <button
                                                onClick={() => removeFromCart(item.product_id, item.variant_label)}
                                                data-testid={`remove-${item.product_id}`}
                                                className="text-white/40 hover:text-red-500"
                                            >
                                                <Trash2 size={16} />
                                            </button>
                                        </div>
                                        <p className="price-display text-[#D4AF37] mt-2">{fmtPrice(item.price, lang)}</p>
                                        <div className="flex items-center gap-3 mt-3">
                                            <button
                                                onClick={() => updateQty(item.product_id, item.variant_label, item.quantity - 1)}
                                                className="w-7 h-7 border border-[#D4AF37]/30 text-white/80 flex items-center justify-center hover:border-[#D4AF37]"
                                            >
                                                <Minus size={12} />
                                            </button>
                                            <span className="text-white text-sm w-6 text-center">{item.quantity}</span>
                                            <button
                                                onClick={() => updateQty(item.product_id, item.variant_label, item.quantity + 1)}
                                                className="w-7 h-7 border border-[#D4AF37]/30 text-white/80 flex items-center justify-center hover:border-[#D4AF37]"
                                            >
                                                <Plus size={12} />
                                            </button>
                                        </div>
                                    </div>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>

                {cart.length > 0 && (
                    <div className="border-t border-[#D4AF37]/20 px-6 py-6 bg-[#0A0A0A]">
                        <div className="flex items-center justify-between mb-5">
                            <span className="overline">{t.cart.subtotal}</span>
                            <span data-testid="cart-subtotal" className="price-display text-2xl text-[#D4AF37]">{fmtPrice(cartTotal, lang)}</span>
                        </div>
                        <button
                            onClick={handleCheckout}
                            data-testid="cart-checkout-btn"
                            className="btn-gold btn-gold-filled w-full"
                        >
                            {t.cart.checkout}
                        </button>
                    </div>
                )}
            </aside>
        </div>
    );
};
